import { useEffect, useMemo, useState } from "react";
import { wfAPI } from "./wfApi";
import { toast } from "sonner";
import { Plus, Trash2, Building2, Pencil, Check, X, Sparkles, Search, GripVertical, Info, AlertTriangle } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// ─── Constants ──────────────────────────────────────────────────────────
const KIND_OPTIONS = [
  { value: "department",  label: "Department",  color: "sky"     },
  { value: "division",    label: "Division",    color: "indigo"  },
  { value: "team",        label: "Team",        color: "emerald" },
  { value: "cost_center", label: "Cost Center", color: "amber"   },
  { value: "virtual",     label: "Virtual",     color: "slate"   },
];
const CAT_OPTIONS = [
  { value: "management",  label: "Management",   color: "violet"  },
  { value: "supervisory", label: "Supervisory",  color: "indigo"  },
  { value: "operational", label: "Operational",  color: "emerald" },
  { value: "corporate",   label: "Corporate",    color: "sky"     },
  { value: "support",     label: "Support",      color: "slate"   },
];
const TONE_CLS = {
  sky:     "bg-sky-50 text-sky-700 border-sky-200",
  indigo:  "bg-indigo-50 text-indigo-700 border-indigo-200",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  amber:   "bg-amber-50 text-amber-800 border-amber-200",
  violet:  "bg-violet-50 text-violet-700 border-violet-200",
  slate:   "bg-slate-100 text-slate-700 border-slate-200",
};

// ─── Helpers ────────────────────────────────────────────────────────────
function toCode(name, taken = new Set(), max = 6) {
  if (!name) return "";
  const clean = String(name).toUpperCase().replace(/[^A-Z0-9\s]/g, "").trim();
  const words = clean.split(/\s+/).filter(Boolean);
  let base;
  if (words.length >= 2) {
    base = words.map((w) => w[0]).join("").slice(0, max);
  } else {
    base = (words[0] || "").slice(0, max);
  }
  if (!base) return "";
  let code = base, i = 2;
  while (taken.has(code)) { code = `${base}${i}`; i++; }
  return code;
}
const kindBadge = (v) => KIND_OPTIONS.find((k) => k.value === v) || KIND_OPTIONS[0];
const catBadge  = (v) => CAT_OPTIONS.find((k) => k.value === v)  || CAT_OPTIONS[2];

// ─── Small subcomponents ────────────────────────────────────────────────
function ToneBadge({ tone, children, testid }) {
  return (
    <span data-testid={testid}
      className={`inline-flex items-center h-[20px] px-2 rounded-full border text-[10.5px] font-medium uppercase tracking-wider ${TONE_CLS[tone] || TONE_CLS.slate}`}>
      {children}
    </span>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────
export default function Organization() {
  const [units, setUnits]           = useState([]);
  const [jobTitles, setJobTitles]   = useState([]);
  const [loading, setLoading]       = useState(true);

  // Filters
  const [uSearch, setUSearch]       = useState("");
  const [uKindFilter, setUKindFilter] = useState("all");
  const [tSearch, setTSearch]       = useState("");
  const [tCatFilter, setTCatFilter] = useState("all");

  // Create-drawers
  const [addUnitOpen, setAddUnitOpen]   = useState(false);
  const [addTitleOpen, setAddTitleOpen] = useState(false);
  const [newUnit, setNewUnit] = useState({ code:"", name:"", kind:"department", parent_id:"", description:"", codeTouched:false });
  const [newTitle, setNewTitle] = useState({ code:"", name:"", category:"operational", description:"", codeTouched:false });

  // Edit-inline state
  const [editUnit, setEditUnit]   = useState(null);
  const [editTitle, setEditTitle] = useState(null);

  // Delete confirm
  const [pendingDeleteUnit, setPendingDeleteUnit] = useState(null);
  const [pendingDeleteTitle, setPendingDeleteTitle] = useState(null);

  // DnD
  const [dragId, setDragId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [u, j] = await Promise.all([wfAPI.orgUnits(), wfAPI.jobTitles()]);
      setUnits(u); setJobTitles(j);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    finally     { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const unitCodes = useMemo(() => new Set(units.map((u) => u.code)), [units]);
  const titleCodes = useMemo(() => new Set(jobTitles.map((t) => t.code)), [jobTitles]);

  // Auto-generate code from name (unless user manually edited it)
  useEffect(() => {
    if (!addUnitOpen || newUnit.codeTouched) return;
    setNewUnit((s) => ({ ...s, code: toCode(s.name, unitCodes) }));
  }, [newUnit.name, addUnitOpen, unitCodes, newUnit.codeTouched]);
  useEffect(() => {
    if (!addTitleOpen || newTitle.codeTouched) return;
    setNewTitle((s) => ({ ...s, code: toCode(s.name, titleCodes) }));
  }, [newTitle.name, addTitleOpen, titleCodes, newTitle.codeTouched]);

  // Filters
  const filteredUnits = useMemo(() => {
    return units.filter((u) => {
      if (uKindFilter !== "all" && u.kind !== uKindFilter) return false;
      if (uSearch && !`${u.name} ${u.code}`.toLowerCase().includes(uSearch.toLowerCase())) return false;
      return true;
    });
  }, [units, uSearch, uKindFilter]);
  const filteredTitles = useMemo(() => {
    return jobTitles.filter((t) => {
      if (tCatFilter !== "all" && t.category !== tCatFilter) return false;
      if (tSearch && !`${t.name} ${t.code}`.toLowerCase().includes(tSearch.toLowerCase())) return false;
      return true;
    });
  }, [jobTitles, tSearch, tCatFilter]);

  // ── Actions ───────────────────────────────────────────────────────
  const createUnit = async () => {
    if (!newUnit.name.trim()) { toast.error("Name is required"); return; }
    const code = newUnit.code || toCode(newUnit.name, unitCodes);
    try {
      await wfAPI.createOrgUnit({
        code, name: newUnit.name, kind: newUnit.kind,
        parent_id: newUnit.parent_id || null,
        description: newUnit.description || null,
      });
      toast.success(`Department "${newUnit.name}" added`);
      setNewUnit({ code:"", name:"", kind:"department", parent_id:"", description:"", codeTouched:false });
      setAddUnitOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const saveUnit = async () => {
    if (!editUnit?.name.trim()) { toast.error("Name required"); return; }
    try {
      await wfAPI.updateOrgUnit(editUnit.id, {
        name: editUnit.name, kind: editUnit.kind,
        parent_id: editUnit.parent_id || null,
        description: editUnit.description || null,
      });
      setEditUnit(null); toast.success("Department updated"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const doDeleteUnit = async () => {
    const u = pendingDeleteUnit; setPendingDeleteUnit(null);
    try { await wfAPI.archiveOrgUnit(u.id); toast.success(`Deleted "${u.name}"`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const createTitle = async () => {
    if (!newTitle.name.trim()) { toast.error("Name is required"); return; }
    const code = newTitle.code || toCode(newTitle.name, titleCodes);
    try {
      await wfAPI.createJobTitle({
        code, name: newTitle.name, category: newTitle.category,
        description: newTitle.description || null,
      });
      toast.success(`Job title "${newTitle.name}" added`);
      setNewTitle({ code:"", name:"", category:"operational", description:"", codeTouched:false });
      setAddTitleOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const saveTitle = async () => {
    if (!editTitle?.name.trim()) { toast.error("Name required"); return; }
    try {
      await wfAPI.updateJobTitle(editTitle.id, {
        name: editTitle.name, category: editTitle.category,
        description: editTitle.description || null,
      });
      setEditTitle(null); toast.success("Job title updated"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const doDeleteTitle = async () => {
    const t = pendingDeleteTitle; setPendingDeleteTitle(null);
    try { await wfAPI.archiveJobTitle(t.id); toast.success(`Deleted "${t.name}"`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const seedPreset = async () => {
    try {
      const r = await wfAPI.seedOrgPreset();
      toast.success(`Added ${r.org_units_added} department(s) & ${r.job_titles_added} job title(s)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Preset failed"); }
  };

  // Drag-and-drop reorder (units only)
  const handleDragStart = (e, id) => { setDragId(id); e.dataTransfer.effectAllowed = "move"; };
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const handleDrop = async (e, targetId) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) return;
    const orig = [...filteredUnits];
    const fromIdx = orig.findIndex((u) => u.id === dragId);
    const toIdx   = orig.findIndex((u) => u.id === targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    const next = [...orig];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    // Optimistic UI: only rows for the filtered view; server persists full order.
    const items = next.map((u, i) => ({ id: u.id, order: i }));
    // Merge back into full units list so non-visible items retain their order.
    const merged = [...units].sort((a,b) => (a.order||0) - (b.order||0));
    const filteredIds = new Set(orig.map((u) => u.id));
    let cursor = 0;
    const newUnits = merged.map((u) => {
      if (!filteredIds.has(u.id)) return u;
      return next[cursor++];
    });
    setUnits(newUnits);
    setDragId(null);
    try { await wfAPI.reorderOrgUnits(items); }
    catch (e) { toast.error("Reorder failed"); load(); }
  };

  return (
    <div className="space-y-4" data-testid="wf-organization">
      {/* Top action bar */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-[19px] font-semibold text-hocp-navy">Organization</h1>
          <p className="text-[12px] text-slate-500">Departments &amp; Job Titles for this hotel.</p>
        </div>
        {(units.length === 0 && jobTitles.length === 0) || (units.length < 8) ? (
          <button data-testid="btn-seed-preset" onClick={seedPreset}
            className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-hocp-mint text-hocp-deepTeal text-[13px] font-medium hover:bg-hocp-mintStrong transition">
            <Sparkles className="w-4 h-4"/> Seed standard hotel setup
          </button>
        ) : null}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* ── DEPARTMENTS ─────────────────────────────────────────────── */}
        <section className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <header className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h2 className="font-display text-[15px] font-semibold text-hocp-navy">Departments</h2>
              <p className="text-[11.5px] text-slate-500">Drag to reorder · reusable across employee forms</p>
            </div>
            <span className="text-[11px] uppercase tracking-wider text-slate-400">{filteredUnits.length} / {units.length}</span>
          </header>

          {/* Filters */}
          <div className="px-5 py-3 border-b border-slate-100 grid grid-cols-[1fr,140px] gap-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
              <Input value={uSearch} onChange={(e) => setUSearch(e.target.value)}
                placeholder="Search departments…" aria-label="Search departments" data-testid="unit-search"
                className="h-8 pl-8 text-[13px] border-slate-200"/>
            </div>
            <Select value={uKindFilter} onValueChange={setUKindFilter}>
              <SelectTrigger className="h-8 text-[12px]" data-testid="unit-kind-filter" aria-label="Filter by kind">
                <SelectValue/>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All kinds</SelectItem>
                {KIND_OPTIONS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* List */}
          <div className="divide-y divide-slate-100 max-h-[440px] overflow-y-auto">
            {loading ? (
              <div className="px-5 py-10 text-center text-[13px] text-slate-400">Loading…</div>
            ) : filteredUnits.length === 0 ? (
              <div className="px-5 py-10 text-center text-[13px] text-slate-400">
                {units.length === 0 ? "No departments yet — click ‘Seed standard hotel setup’ to start." : "No matches."}
              </div>
            ) : filteredUnits.map((u) => {
              const isEditing = editUnit?.id === u.id;
              const b = kindBadge(u.kind);
              return (
                <div key={u.id} data-testid={`org-unit-${u.id}`}
                  draggable={!isEditing}
                  onDragStart={(e) => handleDragStart(e, u.id)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, u.id)}
                  className={`px-4 py-3 flex items-start gap-3 transition ${dragId === u.id ? "opacity-40" : ""} hover:bg-slate-50/60`}>
                  <GripVertical className="w-3.5 h-3.5 text-slate-300 mt-1 cursor-grab shrink-0" strokeWidth={1.5}/>
                  <Building2 className="w-4 h-4 text-slate-400 mt-1 shrink-0" strokeWidth={1.75}/>

                  {isEditing ? (
                    <div className="flex-1 grid grid-cols-1 gap-2">
                      <Input value={editUnit.name}
                        onChange={(e) => setEditUnit({ ...editUnit, name: e.target.value })}
                        data-testid={`edit-unit-name-${u.id}`} aria-label="Department name"
                        className="h-8 text-[13px]"/>
                      <div className="grid grid-cols-2 gap-2">
                        <Select value={editUnit.kind} onValueChange={(v) => setEditUnit({ ...editUnit, kind: v })}>
                          <SelectTrigger className="h-8 text-[12px]"><SelectValue/></SelectTrigger>
                          <SelectContent>
                            {KIND_OPTIONS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Select value={editUnit.parent_id || "none"} onValueChange={(v) => setEditUnit({ ...editUnit, parent_id: v === "none" ? "" : v })}>
                          <SelectTrigger className="h-8 text-[12px]"><SelectValue placeholder="No parent"/></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No parent</SelectItem>
                            {units.filter((x) => x.id !== u.id).map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <Textarea value={editUnit.description || ""} onChange={(e) => setEditUnit({ ...editUnit, description: e.target.value })}
                        rows={2} placeholder="Description (optional)"
                        className="text-[12px]"/>
                      <div className="flex justify-end gap-1">
                        <button onClick={() => setEditUnit(null)} data-testid={`cancel-unit-${u.id}`}
                          className="h-7 px-2 text-slate-400 hover:bg-slate-100 rounded-md inline-flex items-center gap-1 text-[12px]">
                          <X className="w-3.5 h-3.5"/> Cancel
                        </button>
                        <button onClick={saveUnit} data-testid={`save-unit-${u.id}`}
                          className="h-7 px-2 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-md inline-flex items-center gap-1 text-[12px]">
                          <Check className="w-3.5 h-3.5"/> Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[13.5px] font-medium text-hocp-navy">{u.name}</span>
                          <ToneBadge tone={b.color} testid={`unit-kind-${u.id}`}>{b.label}</ToneBadge>
                          {u.usage_count > 0 && (
                            <ToneBadge tone="slate">{u.usage_count} {u.usage_count === 1 ? "person" : "people"}</ToneBadge>
                          )}
                          {u.is_system && <ToneBadge tone="amber">System</ToneBadge>}
                        </div>
                        <div className="mt-0.5 text-[11.5px] text-slate-500 mono">{u.code}</div>
                        {u.description && <div className="mt-1 text-[12px] text-slate-600 leading-snug">{u.description}</div>}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => setEditUnit({ id: u.id, name: u.name, kind: u.kind, parent_id: u.parent_id || "", description: u.description || "" })}
                          data-testid={`edit-unit-${u.id}`}
                          className="p-1.5 text-slate-400 hover:text-hocp-navy hover:bg-slate-100 rounded-md" title="Edit">
                          <Pencil className="w-3.5 h-3.5"/>
                        </button>
                        <button onClick={() => setPendingDeleteUnit(u)} data-testid={`archive-unit-${u.id}`}
                          className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-md" title="Delete">
                          <Trash2 className="w-3.5 h-3.5"/>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/* Add-new footer */}
          {!addUnitOpen ? (
            <button onClick={() => setAddUnitOpen(true)} data-testid="open-add-unit"
              className="w-full py-3 text-[13px] text-hocp-navy font-medium border-t border-slate-100 hover:bg-slate-50 inline-flex items-center justify-center gap-1">
              <Plus className="w-4 h-4"/> Add department
            </button>
          ) : (
            <div className="p-4 border-t border-slate-100 bg-slate-50/50 space-y-2">
              <div className="grid grid-cols-[1fr,120px] gap-2">
                <label className="block"><span className="block text-[11px] font-medium text-slate-600 mb-1">Department name</span>
                  <Input placeholder="e.g. Concierge"
                    value={newUnit.name} onChange={(e) => setNewUnit({ ...newUnit, name: e.target.value })}
                    data-testid="new-unit-name" className="h-8 text-[13px]" autoFocus/></label>
                <label className="block"><span className="block text-[11px] font-medium text-slate-600 mb-1">Code</span>
                  <Input placeholder="e.g. CONC" value={newUnit.code}
                    onChange={(e) => setNewUnit({ ...newUnit, code: e.target.value.toUpperCase(), codeTouched:true })}
                    data-testid="new-unit-code" className="h-8 text-[13px] mono"/></label>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><span className="block text-[11px] font-medium text-slate-600 mb-1">Kind</span>
                  <Select value={newUnit.kind} onValueChange={(v) => setNewUnit({ ...newUnit, kind: v })}>
                    <SelectTrigger className="h-8 text-[12px]" data-testid="new-unit-kind" aria-label="Kind"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {KIND_OPTIONS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div><span className="block text-[11px] font-medium text-slate-600 mb-1">Parent department</span>
                  <Select value={newUnit.parent_id || "none"} onValueChange={(v) => setNewUnit({ ...newUnit, parent_id: v === "none" ? "" : v })}>
                    <SelectTrigger className="h-8 text-[12px]" data-testid="new-unit-parent" aria-label="Parent department"><SelectValue placeholder="No parent"/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No parent</SelectItem>
                      {units.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
              </div>
              <label className="block"><span className="block text-[11px] font-medium text-slate-600 mb-1">Description <span className="text-slate-400 font-normal">(optional)</span></span>
                <Textarea placeholder="Short description of this department" rows={2}
                  value={newUnit.description}
                  onChange={(e) => setNewUnit({ ...newUnit, description: e.target.value })}
                  data-testid="new-unit-description" className="text-[12px]"/></label>
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => { setAddUnitOpen(false); setNewUnit({ code:"", name:"", kind:"department", parent_id:"", description:"", codeTouched:false }); }}
                  className="h-8 px-3 text-[13px] text-slate-500 hover:bg-slate-100 rounded-md">Cancel</button>
                <button onClick={createUnit} data-testid="create-org-unit"
                  className="h-8 px-4 rounded-md bg-hocp-gold text-hocp-eerie text-[13px] font-medium inline-flex items-center gap-1">
                  <Plus className="w-3.5 h-3.5"/> Create
                </button>
              </div>
            </div>
          )}
        </section>

        {/* ── JOB TITLES ─────────────────────────────────────────────── */}
        <section className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <header className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h2 className="font-display text-[15px] font-semibold text-hocp-navy">Job Titles</h2>
              <p className="text-[11.5px] text-slate-500 flex items-center gap-1">
                <Info className="w-3 h-3"/> Display / HR only — never grants permissions.
              </p>
            </div>
            <span className="text-[11px] uppercase tracking-wider text-slate-400">{filteredTitles.length} / {jobTitles.length}</span>
          </header>

          <div className="px-5 py-3 border-b border-slate-100 grid grid-cols-[1fr,140px] gap-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
              <Input value={tSearch} onChange={(e) => setTSearch(e.target.value)}
                placeholder="Search titles…" aria-label="Search job titles" data-testid="title-search"
                className="h-8 pl-8 text-[13px] border-slate-200"/>
            </div>
            <Select value={tCatFilter} onValueChange={setTCatFilter}>
              <SelectTrigger className="h-8 text-[12px]" data-testid="title-cat-filter" aria-label="Filter by category"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {CAT_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="divide-y divide-slate-100 max-h-[440px] overflow-y-auto">
            {loading ? (
              <div className="px-5 py-10 text-center text-[13px] text-slate-400">Loading…</div>
            ) : filteredTitles.length === 0 ? (
              <div className="px-5 py-10 text-center text-[13px] text-slate-400">
                {jobTitles.length === 0 ? "No job titles yet — click ‘Seed standard hotel setup’ to start." : "No matches."}
              </div>
            ) : filteredTitles.map((t) => {
              const isEditing = editTitle?.id === t.id;
              const b = catBadge(t.category);
              return (
                <div key={t.id} data-testid={`job-title-${t.id}`}
                  className="px-4 py-3 flex items-start gap-3 hover:bg-slate-50/60">
                  {isEditing ? (
                    <div className="flex-1 grid grid-cols-1 gap-2">
                      <Input value={editTitle.name}
                        onChange={(e) => setEditTitle({ ...editTitle, name: e.target.value })}
                        data-testid={`edit-title-name-${t.id}`} aria-label="Job title" className="h-8 text-[13px]"/>
                      <Select value={editTitle.category} onValueChange={(v) => setEditTitle({ ...editTitle, category: v })}>
                        <SelectTrigger className="h-8 text-[12px]"><SelectValue/></SelectTrigger>
                        <SelectContent>{CAT_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <Textarea value={editTitle.description || ""} onChange={(e) => setEditTitle({ ...editTitle, description: e.target.value })}
                        rows={2} placeholder="Description (optional)" className="text-[12px]"/>
                      <div className="flex justify-end gap-1">
                        <button onClick={() => setEditTitle(null)} className="h-7 px-2 text-slate-400 hover:bg-slate-100 rounded-md inline-flex items-center gap-1 text-[12px]"><X className="w-3.5 h-3.5"/> Cancel</button>
                        <button onClick={saveTitle} data-testid={`save-title-${t.id}`} className="h-7 px-2 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-md inline-flex items-center gap-1 text-[12px]"><Check className="w-3.5 h-3.5"/> Save</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[13.5px] font-medium text-hocp-navy">{t.name}</span>
                          <ToneBadge tone={b.color} testid={`title-cat-${t.id}`}>{b.label}</ToneBadge>
                          {t.usage_count > 0 && (
                            <ToneBadge tone="slate">{t.usage_count} {t.usage_count === 1 ? "person" : "people"}</ToneBadge>
                          )}
                          {t.is_system && <ToneBadge tone="amber">System</ToneBadge>}
                        </div>
                        <div className="mt-0.5 text-[11.5px] text-slate-500 mono">{t.code}</div>
                        {t.description && <div className="mt-1 text-[12px] text-slate-600 leading-snug">{t.description}</div>}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => setEditTitle({ id: t.id, name: t.name, category: t.category, description: t.description || "" })}
                          data-testid={`edit-title-${t.id}`}
                          className="p-1.5 text-slate-400 hover:text-hocp-navy hover:bg-slate-100 rounded-md" title="Edit">
                          <Pencil className="w-3.5 h-3.5"/>
                        </button>
                        <button onClick={() => setPendingDeleteTitle(t)} data-testid={`archive-title-${t.id}`}
                          className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-md" title="Delete">
                          <Trash2 className="w-3.5 h-3.5"/>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {!addTitleOpen ? (
            <button onClick={() => setAddTitleOpen(true)} data-testid="open-add-title"
              className="w-full py-3 text-[13px] text-hocp-navy font-medium border-t border-slate-100 hover:bg-slate-50 inline-flex items-center justify-center gap-1">
              <Plus className="w-4 h-4"/> Add job title
            </button>
          ) : (
            <div className="p-4 border-t border-slate-100 bg-slate-50/50 space-y-2">
              <div className="grid grid-cols-[1fr,120px] gap-2">
                <label className="block"><span className="block text-[11px] font-medium text-slate-600 mb-1">Job title</span>
                  <Input placeholder="e.g. Reservations Agent"
                    value={newTitle.name} onChange={(e) => setNewTitle({ ...newTitle, name: e.target.value })}
                    data-testid="new-title-name" className="h-8 text-[13px]" autoFocus/></label>
                <label className="block"><span className="block text-[11px] font-medium text-slate-600 mb-1">Code</span>
                  <Input placeholder="e.g. RESAGT" value={newTitle.code}
                    onChange={(e) => setNewTitle({ ...newTitle, code: e.target.value.toUpperCase(), codeTouched:true })}
                    className="h-8 text-[13px] mono"/></label>
              </div>
              <div><span className="block text-[11px] font-medium text-slate-600 mb-1">Category</span>
                <Select value={newTitle.category} onValueChange={(v) => setNewTitle({ ...newTitle, category: v })}>
                  <SelectTrigger className="h-8 text-[12px]" data-testid="new-title-cat" aria-label="Category"><SelectValue/></SelectTrigger>
                  <SelectContent>{CAT_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select></div>
              <label className="block"><span className="block text-[11px] font-medium text-slate-600 mb-1">Description <span className="text-slate-400 font-normal">(optional)</span></span>
                <Textarea placeholder="Short description of this job title" rows={2}
                  value={newTitle.description}
                  onChange={(e) => setNewTitle({ ...newTitle, description: e.target.value })}
                  className="text-[12px]"/></label>
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => { setAddTitleOpen(false); setNewTitle({ code:"", name:"", category:"operational", description:"", codeTouched:false }); }}
                  className="h-8 px-3 text-[13px] text-slate-500 hover:bg-slate-100 rounded-md">Cancel</button>
                <button onClick={createTitle} data-testid="create-job-title"
                  className="h-8 px-4 rounded-md bg-hocp-gold text-hocp-eerie text-[13px] font-medium inline-flex items-center gap-1">
                  <Plus className="w-3.5 h-3.5"/> Create
                </button>
              </div>
            </div>
          )}
        </section>
      </div>

      {/* Delete confirms */}
      <AlertDialog open={!!pendingDeleteUnit} onOpenChange={(v) => !v && setPendingDeleteUnit(null)}>
        <AlertDialogContent data-testid="confirm-delete-unit">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete department &ldquo;{pendingDeleteUnit?.name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDeleteUnit?.usage_count > 0
                ? <span className="text-rose-600">Blocked — {pendingDeleteUnit.usage_count} active employment(s) reference this department. Move them first.</span>
                : (
                  <>
                    {pendingDeleteUnit?.is_system && (
                      <span className="block mb-1 text-amber-700 font-medium"><AlertTriangle size={14} strokeWidth={1.5} className="inline mr-1 -mt-0.5" />This is a system-seeded department.</span>
                    )}
                    This archives the department. Historical employment records remain intact.
                  </>
                )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            {(!pendingDeleteUnit?.usage_count) && (
              <AlertDialogAction onClick={doDeleteUnit} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-unit-btn">Delete</AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pendingDeleteTitle} onOpenChange={(v) => !v && setPendingDeleteTitle(null)}>
        <AlertDialogContent data-testid="confirm-delete-title">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete job title &ldquo;{pendingDeleteTitle?.name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDeleteTitle?.usage_count > 0
                ? <span className="text-rose-600">Blocked — {pendingDeleteTitle.usage_count} active employment(s) use this title. Reassign them first.</span>
                : (
                  <>
                    {pendingDeleteTitle?.is_system && (
                      <span className="block mb-1 text-amber-700 font-medium"><AlertTriangle size={14} strokeWidth={1.5} className="inline mr-1 -mt-0.5" />This is a system-seeded job title.</span>
                    )}
                    This archives the title. Historical employment records remain intact.
                  </>
                )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            {(!pendingDeleteTitle?.usage_count) && (
              <AlertDialogAction onClick={doDeleteTitle} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-title-btn">Delete</AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
